import { Upload } from "lucide-react";
import { FeedbackBanner } from "@/core/components/feedback-banner";
import { LoadingSpinner } from "@/core/components/loading-spinner";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import { getErrorMessage } from "@/core/api/errors";
import { formatMoneyCents } from "@/core/utils/money";
import { useStAdjustmentLines } from "./query";
import { TechnicianAdjustmentLine } from "./technician-adjustment-line";
import { TechnicianSidebarHeader } from "./technician-sidebar-header";
import type { StAdjustmentTechnicianGroup } from "./types";
import { isPushableGroup, proposalKey } from "./utils";

interface TechnicianLinesSidebarProps {
  businessId: string | null;
  runId: string | null;
  // The rollup row this drilled down from. Null closes the panel — the shell
  // stays mounted, so the contents simply disappear.
  group: StAdjustmentTechnicianGroup | null;
  busy: boolean;
  onClose: () => void;
  onPushTechnician: (group: StAdjustmentTechnicianGroup) => void;
}

// The lines behind one rollup row. Fetched filtered by employee_id rather than
// sliced client-side: the run's lines are paged 50 at a time out of potentially
// thousands, so a technician's rows are mostly not in any page we hold.
export const TechnicianLinesSidebar = ({
  businessId,
  runId,
  group,
  busy,
  onClose,
  onPushTechnician,
}: TechnicianLinesSidebarProps) => {
  const employeeId = group?.employee_id ?? null;
  const {
    data,
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useStAdjustmentLines(businessId, runId, !!employeeId, employeeId);

  if (!group) return null;

  const pages = data?.pages ?? [];
  const lines = pages.flatMap((page) => page.items);
  const totalCount = pages[0]?.total_count ?? 0;
  const pushable = isPushableGroup(group);

  return (
    <Stack gap="none" fullWidth className="h-full">
      <TechnicianSidebarHeader group={group} onClose={onClose} />

      <Stack gap="md" fullWidth className="min-h-0 flex-1 overflow-y-auto p-5">
        {isLoading && <LoadingSpinner className="h-32" color="primary" />}

        {error && !isLoading && (
          <FeedbackBanner variant="error" iconSize="sm">
            <Typography variant="bodyW2">{getErrorMessage(error)}</Typography>
          </FeedbackBanner>
        )}

        {!isLoading &&
          lines.map((line) => (
            <TechnicianAdjustmentLine
              key={proposalKey(line)}
              jobNumber={line.job_number}
              amountCents={line.amount_cents}
              category={line.category}
              memo={line.memo}
              alreadyPushed={line.already_pushed}
            />
          ))}

        {hasNextPage && (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fetchNextPage()}
            loading={isFetchingNextPage}
            // fetchNextPage would cancel an in-flight refetch and leave stale
            // rows on screen.
            disabled={isFetching}
          >
            Load more
          </Button>
        )}

        {!isLoading && !error && (
          <Typography variant="captionW1" textColor="secondary">
            Showing {lines.length} of {totalCount}
          </Typography>
        )}
      </Stack>

      {pushable && (
        <Stack
          fullWidth
          className="shrink-0 border-t border-stroke-tertiary px-5 py-4"
        >
          <Button
            fullWidth
            leftIcon={<Upload className="h-4 w-4" />}
            loading={busy}
            disabled={busy}
            onClick={() => onPushTechnician(group)}
          >
            Push {group.pending} ·{" "}
            {formatMoneyCents(group.pending_amount_cents) ?? "$0"}
          </Button>
        </Stack>
      )}
    </Stack>
  );
};
